import React, { useState, useRef } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css"; // Import styles
import { ArrowLeft, Save, Calendar, Tag, Plus, Trash2, Columns, Rows, MoreHorizontal } from "lucide-react";

/**
 * ExpandedNoteEditor
 * A full-page rich text editor for notes.
 *
 * Props:
 * - note: The note object to edit (or empty object for new note)
 * - onBack: Callback to return to the list view
 * - onSave: Callback to save the note (receives updated note object)
 */
const ExpandedNoteEditor = ({ note, onBack, onSave }) => {
    const [formData, setFormData] = useState({
        title: note.title || "",
        content: note.content || "",
        category: note.category || "Allgemein",
        noteDate: note.noteDate ? note.noteDate.split("T")[0] : new Date().toISOString().split("T")[0],
        isGeneral: note.isGeneral || false,
        id: note.id || null, // Preserve ID if editing
    });

    const quillRef = useRef(null);

    const modules = {
        table: true,
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['table']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image', 'color', 'background', 'font', 'align',
        'table'
    ];

    const handleSave = () => {
        if (!formData.title.trim()) {
            alert("Bitte geben Sie einen Titel ein.");
            return;
        }
        const noteToSave = {
            ...formData,
            workspaceId: note.workspaceId
        };
        onSave(noteToSave);
    };

    // Table Helper Functions
    const getTableModule = () => {
        if (!quillRef.current) return null;
        const quill = quillRef.current.getEditor();
        return quill.getModule('table');
    };

    const insertRow = () => getTableModule()?.insertRowBelow();
    const insertCol = () => getTableModule()?.insertColumnRight();
    const deleteRow = () => getTableModule()?.deleteRow();
    const deleteCol = () => getTableModule()?.deleteColumn();
    const deleteTable = () => getTableModule()?.deleteTable();

    return (
        <div className="container-fluid max-width-xl h-100 d-flex flex-column bg-white shadow-sm rounded-4 p-0 overflow-hidden">
            {/* --- HEADER --- */}
            <div className="d-flex align-items-center justify-content-between p-4 border-bottom bg-light">
                <div className="d-flex align-items-center gap-3">
                    <button className="btn btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center" onClick={onBack} style={{ width: '40px', height: '40px' }}>
                        <ArrowLeft size={20} />
                    </button>
                    <h4 className="mb-0 fw-bold text-dark">Notiz bearbeiten</h4>
                </div>
                <button className="btn btn-primary rounded-3 px-4 fw-bold d-flex align-items-center gap-2" onClick={handleSave}>
                    <Save size={18} /> Speichern
                </button>
            </div>

            {/* --- CONTENT INPUTS --- */}
            <div className="p-4 overflow-auto flex-grow-1">
                <div className="row g-4 h-100">
                    <div className="col-12">
                        <input
                            type="text"
                            className="form-control form-control-lg border-0 bg-transparent fw-bold display-6 px-0 mb-3"
                            placeholder="Titel hier eingeben..."
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                    </div>

                    <div className="col-md-4">
                        <label className="small fw-bold text-muted mb-1 ml-1 d-flex align-items-center gap-1"><Tag size={12} /> Kategorie</label>
                        <input
                            className="form-control bg-light border-0 rounded-3"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            placeholder="Kategorie..."
                        />
                    </div>

                    <div className="col-md-4">
                        <label className="small fw-bold text-muted mb-1 ml-1 d-flex align-items-center gap-1"><Calendar size={12} /> Datum</label>
                        <input
                            type="date"
                            className="form-control bg-light border-0 rounded-3"
                            value={formData.noteDate}
                            onChange={(e) => setFormData({ ...formData, noteDate: e.target.value })}
                        />
                    </div>

                    <div className="col-md-4 d-flex align-items-end">
                        <div className="form-check form-switch bg-light w-100 p-2 rounded-3 px-5 border-0 d-flex align-items-center gap-2">
                            <input
                                className="form-check-input my-0"
                                type="checkbox"
                                id="pinSwitchExpanded"
                                checked={formData.isGeneral}
                                onChange={(e) => setFormData({ ...formData, isGeneral: e.target.checked })}
                            />
                            <label className="form-check-label small fw-bold text-muted mb-0 cursor-pointer" htmlFor="pinSwitchExpanded">
                                Oben anheften
                            </label>
                        </div>
                    </div>

                    {/* EDITOR AREA */}
                    <div className="col-12 d-flex flex-column flex-grow-1 mt-4" style={{ minHeight: '500px' }}>
                        <div className="d-flex justify-content-between align-items-end mb-2">
                            <label className="small fw-bold text-muted ml-1">Inhalt</label>
                            <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-light border d-flex align-items-center gap-1" onClick={insertRow} title="Zeile einfügen">
                                    <Rows size={14} /><Plus size={10} />
                                </button>
                                <button className="btn btn-sm btn-light border d-flex align-items-center gap-1" onClick={insertCol} title="Spalte einfügen">
                                    <Columns size={14} /><Plus size={10} />
                                </button>
                                <div className="vr mx-1"></div>
                                <button className="btn btn-sm btn-light border text-danger" onClick={deleteRow} title="Zeile löschen">
                                    <Rows size={14} /><Trash2 size={10} />
                                </button>
                                <button className="btn btn-sm btn-light border text-danger" onClick={deleteCol} title="Spalte löschen">
                                    <Columns size={14} /><Trash2 size={10} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-grow-1 bg-white rounded-3 overflow-hidden d-flex flex-column h-100 editor-container-custom">
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={formData.content}
                                onChange={(content) => setFormData({ ...formData, content: content })}
                                modules={modules}
                                formats={formats}
                                className="h-100 d-flex flex-column"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
        .editor-container-custom .quill {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
        .editor-container-custom .ql-container {
            flex-grow: 1;
            overflow-y: auto;
            border-bottom-left-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
            background: #f8f9fa; /* Slight off-white background for writing area */
            border: none !important;
        }
        .editor-container-custom .ql-toolbar {
            border-top-left-radius: 0.5rem;
            border-top-right-radius: 0.5rem;
            border: none !important;
            background: #f1f3f5;
            border-bottom: 1px solid #e9ecef !important;
        }
        .editor-container-custom .ql-editor {
            font-size: 1.05rem;
            line-height: 1.6;
            min-height: 200px;
        }
      `}</style>
        </div>
    );
};

export default ExpandedNoteEditor;
